// store/Auth/authSlice.js
import { createSlice } from "@reduxjs/toolkit";
import { verifyUser, logoutUser, markTrainingVideoComplete } from "./authThunks";

const initialState = {
  loginMethod: null,
  permissions: [],
  loading: false,
  error: null,
  user: null,

  // Has the backend verification completed at least once?
  initialized: false,

  // Did we *attempt* verifyUser at least once?
  verificationAttempted: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * Called when Auth0 says "not authenticated".
     * Stops the AppContent spinner and allows PublicRoute to redirect to login.
     */
    skipVerification(state) {
      state.initialized = true;
      state.loading = false;
      state.verificationAttempted = true;
      state.user = null;
      state.permissions = [];
      state.loginMethod = null;
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // 1️⃣ verifyUser: starting
      .addCase(verifyUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.verificationAttempted = true;
        // DO NOT reset initialized — prevents infinite spinner
      })

      // 2️⃣ verifyUser: success
      .addCase(verifyUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.verificationAttempted = true;

        // Backend returns: { success, message, data: { user fields } }
        const backendUser = action.payload?.data;

        // If backendUser missing → treat as failed verification
        if (!backendUser) {
          state.user = null;
          state.permissions = [];
          state.loginMethod = null;
          state.initialized = true;
          state.error = "Backend returned no user in verifyUser";
          return;
        }

        // Map backend response to state
        state.user = backendUser;
        state.permissions = backendUser.permissions || [];
        state.loginMethod = "auth0";
        state.initialized = true;
      })


      // 3️⃣ verifyUser: failure
      .addCase(verifyUser.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.permissions = [];
        state.loginMethod = null;
        state.verificationAttempted = true;

        // Mark as initialized so AppContent stops spinner
        state.initialized = true;

        state.error =
          action.payload || action.error?.message || "Verification failed";
      })

      // 4️⃣ logoutUser
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.permissions = [];
        state.loginMethod = null;
        state.error = null;
        // keep initialized = true so UI doesn't spin
      })

      // 5️⃣ training video completion
      .addCase(markTrainingVideoComplete.fulfilled, (state, action) => {
        const updatedUser = action.payload;
        state.user = updatedUser;
        state.permissions = updatedUser.permissions || [];
      });
  },
});

export const { skipVerification } = authSlice.actions;
export default authSlice.reducer;
